import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, TextField } from '@material-ui/core';
import { deleteSubmissionGroup } from '@services/submissionGroupService';
import { setPartialFilters } from '@actions/lettersPageActions';
import {
  canRemoveSubmissionGroupSelector,
  canUpdateSubmissionGroupSelector,
  getSubmissionGroupIsUpdating,
} from '@selectors/submissionGroups';
import { translate } from '@utils';
import { ALL_GROUPS_OPTION, groupsFilterOptions } from '../constants';
import { MgState, SubmissionQueryInterface } from '@types';
import { TrashIcon, EditMajorIcon, WellDone } from '@images/icons';
import {
  makeDeleteSubmissionGroup,
  makeUpdateSubmissionGroup,
} from '@actions/submissionGroupsActions';

export const EditGroupField = ({
  groupId,
  groupName,
  isEditable,
  toggleEdit,
  submissions,
  componentKey,
  submissionGroups,
}) => {
  const dispatch = useDispatch();
  const defaultName = groupName;
  const [editValue, setEditValue] = useState(groupName);
  const [isError, setIsError] = useState(false);

  const { groupId: selectedGroup } = useSelector<MgState, SubmissionQueryInterface>(
    ({ lettersPage }) => lettersPage.filters
  );
  const canUpdateGroup = useSelector(canUpdateSubmissionGroupSelector);
  const canRemoveGroup = useSelector(canRemoveSubmissionGroupSelector);

  const isUpdating = useSelector(getSubmissionGroupIsUpdating);

  const handleChangeGroup = (value) => {
    const updatedValue = value === ALL_GROUPS_OPTION ? '' : value;
    dispatch(setPartialFilters({ groupId: updatedValue }));
  };

  const handleDeleteGroup = useCallback(
    (id: string) => {
      dispatch(makeDeleteSubmissionGroup(id));
      if (selectedGroup === id) {
        handleChangeGroup(groupsFilterOptions[0]);
      }
    },

    [deleteSubmissionGroup, handleChangeGroup, selectedGroup, toggleEdit, dispatch]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trimStart();
      setEditValue(value);
    },
    [editValue]
  );

  const handleUpdateGroup = useCallback(() => {
    if (defaultName !== editValue) {
      dispatch(makeUpdateSubmissionGroup(groupId, { name: editValue }));
    }
    toggleEdit();
  }, [dispatch, editValue]);

  useEffect(() => {
    setIsError(
      submissionGroups.some((group) => group.name === editValue) && editValue !== defaultName
    );
  }, [editValue]);

  return (
    <div className="edit-group-item" key={componentKey} data-testid={`edit-group-item-${groupName}`}>
      {canRemoveGroup && (
        <Button
          size="large"
          onClick={() => handleDeleteGroup(groupId)}
          disabled={Boolean(submissions.length)}
          disableRipple
          data-testid={`${groupName}-delete-group-button`}
        >
          <TrashIcon />
        </Button>
      )}

      {isEditable ? (
        <TextField
          value={editValue}
          placeholder={translate('modal_input_name')}
          className="edit-group-field"
          onChange={handleInputChange}
          helperText={isError ? translate('group_name_exist') : ''}
          error={isError}
          autoFocus
          id="standard-basic"
        />
      ) : (
        <div className="edit-group-field">{groupName}</div>
      )}
      {isEditable ? (
        <Button
          size="large"
          color={'primary'}
          disabled={!editValue || isUpdating || isError}
          onClick={handleUpdateGroup}
          disableRipple
          data-testid={`${groupName}-edit-group-done`}
        >
          <WellDone />
        </Button>
      ) : (
        canUpdateGroup && (
          <Button size="large" color={'primary'} onClick={toggleEdit} disableRipple data-testid={`${groupName}-edit-group-button`}>
            <EditMajorIcon />
          </Button>
        )
      )}
    </div>
  );
};
